import {
  addMinutes,
  addMilliseconds,
  differenceInMinutes,
  differenceInMilliseconds,
  format,
  isAfter,
  parse,
  isValid,
  isWithinInterval,
  eachDayOfInterval,
  isEqual
} from 'date-fns';

/**
 * Combina la fecha y la hora en un solo objeto Date
 * @param {Date} date La fecha a combinar
 * @param {Date} time La hora a combinar
 * @returns Un objeto Date con la fecha y la hora combinadas
 */
export function combineDateAndTime(date: Date, time: Date): Date {
  const [combineDate, combineTime] = [new Date(date), new Date(time)];
  return new Date(
    combineDate.getFullYear(),
    combineDate.getMonth(),
    combineDate.getDate(),
    combineTime.getHours(),
    combineTime.getMinutes()
  );
}

/**
 * Construye un array de segmentos de tiempo a partir de un límite inferior y superior y un tamaño de segmento en minutos
 * @param {Date} limInf Límite inferior
 * @param {Date} limSup Límite superior
 * @param {number} segmentMinutesSize Tamaño de cada segmento en minutos
 * @returns {Date[]} Un array de segmentos de tiempo
 */
export function createDateSegments(
  limInf: Date,
  limSup: Date,
  segmentMinutesSize: number
): Date[] {
  const segments: Date[] = [];
  if (isAfter(limInf, limSup)) {
    return segments;
  }

  let current = new Date(limInf);
  const currentSup = new Date(limSup);
  segments.push(current);

  while (true) {
    const next = new Date(current);
    next.setMinutes(current.getMinutes() + segmentMinutesSize);

    if (next > currentSup) break;

    segments.push(next);
    current = next;
  }

  return segments;
}

/**
 * Formatea una fecha en un string con formato 24 horas
 * @param {Date} date fecha a convertir
 * @returns {string} hora en formato HH:mm
 */
export function dateToStringTime(date: Date): string {
  return format(date, 'HH:mm');
}

/**
 * Crea una fecha con base en los milisegundos
 * @param {number} mills Los milisegundos
 * @returns {Date} La fecha creada
 */
export function fromMills(mills: number): Date {
  return new Date(mills);
}

/**
 * Obtiene la diferencia en minutos entre dos fechas
 * @param {Date} date1 Fecha 1
 * @param {Date} date2 Fecha 2
 * @returns {number} Diferencia en minutos
 */
export function getMinutesDifference(date1: Date, date2: Date): number {
  return differenceInMinutes(date1, date2);
}

/**
 * Obtiene la diferencia en Milliseconds entre dos fechas
 * @param {Date} date1 Fecha 1
 * @param {Date} date2 Fecha 2
 * @returns {number} Diferencia en Milliseconds
 */
export function getMillisecondsDifference(date1: Date, date2: Date): number {
  return differenceInMilliseconds(date1, date2);
}

/**
 * Extrae año, mes y día de una fecha (string o Date)
 * @param date Fecha en formato string 'YYYY-MM-DD' o objeto Date
 * @returns Array [year, month, day]
 */
function extractDateComponents(date: string | Date): [number, number, number] {
  if (typeof date === 'string') {
    return date.split('-').map((part) => parseInt(part, 10)) as [
      number,
      number,
      number
    ];
  }
  return [date.getFullYear(), date.getMonth() + 1, date.getDate()];
}

/**
 * Convierte un string a un objeto Date
 * @param {string} date string a convertir en formato YYYY-MM-DD
 * @returns Un objeto Date
 */
export function stringToDate(date: string): Date {
  const [year, month, day] = extractDateComponents(date);
  return new Date(year, month - 1, day);
}

/**
 * Convierte un string a un objeto Date con hora
 * @param date string a convertir en formato YYYY-MM-DD o objeto Date
 * @param time objeto Date que contiene la hora
 * @returns Un objeto Date con la fecha y la hora combinadas
 */
export function stringToDateWithTime(date: string | Date, time: Date): Date {
  const [year, month, day] = extractDateComponents(date);
  return new Date(year, month - 1, day, time.getHours(), time.getMinutes());
}

/**
 * Convierte un objeto Date a una cadena de texto en formato 'YYYY-MM-DD'.
 *
 * @param date - El objeto Date que se desea convertir a cadena.
 * @returns La fecha formateada como una cadena en formato 'YYYY-MM-DD'.
 */
export function dateToString(date: Date): string {
  return format(date, 'yyyy-MM-dd');
}

/**
 * Convierte un objeto Date a una cadena de texto en formato 'YYYY-MM-DD HH:mm'.
 *
 * @param {Date} date - El objeto Date que se desea convertir a cadena.
 * @returns {string} La fecha formateada
 */
export function dateWithTimeToString(date: Date): string {
  return format(date, 'yyyy-MM-dd HH:mm');
}

/**
 * Agrega minutos a una fecha
 * @param {Date} date La fecha a la que se le agregarán minutos
 * @param {number} minutes La cantidad de minutos a agregar
 * @returns {Date} La nueva fecha con minutos agregados
 */
export function dateAddMinutes(date: Date, minutes: number): Date {
  return addMinutes(date, minutes);
}

/**
 * Valida si un string cumple con un formato de fecha específico y devuelve un objeto Date si es válido.
 *
 * @param dateString El string que representa la fecha.
 * @param formatString El formato esperado del string (ej: 'yyyy-MM-dd', 'dd/MM/yyyy HH:mm').
 * @returns Un objeto Date si el string es válido y cumple con el formato, o null si no lo es.
 */
export function parseDateFromString(
  dateString: string,
  formatString: string
): Date | null {
  try {
    const parsedDate = parse(dateString, formatString, new Date());
    return isValid(parsedDate) ? parsedDate : null;
  } catch {
    return null;
  }
}

/**
 * Agrega millisegundos a una fecha
 * @param {Date} date La fecha a la que se le agregarán millisegundos
 * @param {number} milliseconds La cantidad de millisegundos a agregar
 * @returns {Date} La nueva fecha con millisegundos agregados
 */
export function dateAddMilliseconds(date: Date, milliseconds: number): Date {
  return addMilliseconds(date, milliseconds);
}

/**
 * Verifica si las horas de una fecha está dentro de un rango de horas de fechas
 * @param time Objeto Date que representa la hora a verificar
 * @param startDate Objeto Date que representa la fecha de inicio del rango
 * @param endDate Objeto Date que representa la fecha de fin del rango
 * @returns {boolean} true si la hora está dentro del rango, false en caso contrario
 */
export function timeExistsInDateRange(
  time: Date,
  startDate: Date,
  endDate: Date
): boolean {
  return isWithinInterval(time, { start: startDate, end: endDate });
}

/**
 * Devuelve un array de fechas que representan cada día entre dos fechas dadas.
 * @param {Date} start La fecha de inicio del rango.
 * @param {Date} end La fecha de fin del rango.
 * @returns {Date[]} Un array de objetos Date que representan cada día en el rango.
 */
export function getEachDayOfRange(start: Date, end: Date): Date[] {
  if (isAfter(start, end)) {
    return [];
  }

  return eachDayOfInterval({
    start: new Date(start),
    end: new Date(end)
  });
}

/**
 * Verifica si una fecha es posterior o igual a otra.
 * @param {Date} date1 La primera fecha a comparar.
 * @param {Date} date2 La segunda fecha a comparar.
 * @returns {Boolean} true si date1 es posterior o igual a date2, false en caso contrario.
 */
export function isAfterOrEqualsTo(date1?: Date, date2?: Date): boolean {
  if (!date1) {
    return false;
  }

  if (!date2) {
    return true;
  }

  return isAfter(date1, date2) || isEqual(date1, date2);
}
